import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Local types for exports
export interface JourneyClient {
  id_loja?: number;
  id?: number;
  razao_social?: string;
  cliente?: string;
  nome?: string;
  consultor?: string;
  complianceRate?: number;
  journeyScore?: number;
  totalPending?: number;
  priorityScore?: number;
  priorityReasons?: string | string[];
  daysSinceLastMeeting?: number | null;
  riskLevel?: string;
  expectedMeetings?: number;
  completedMeetings?: number;
  isMissingRD?: boolean;
  isMissingRE45?: boolean;
  isMissingRC?: boolean;
  isMissingRAE?: boolean;
}

export interface JourneyPriority {
  critical: JourneyClient[];
  warning: JourneyClient[];
  healthy: JourneyClient[];
  unmonitored: JourneyClient[];
}

export interface JourneySummary {
  totalClients: number;
  monitorados: number;
  clientsHealthy: number;
  clientsAttention: number;
  clientsCritical: number;
  clientsAbandoned: number;
  averageJourneyScore: number;
  averagePendingPerClient: number;
  averageDaysWithoutMeeting: number;
  medianDaysWithoutMeeting: number;
  totalMetasPrevistas: number;
  totalMetasCumpridas: number;
  meetingComplianceRate: number;
  clientsWithoutRD: number;
  clientsWithoutRELast45Days: number;
  clientsWithoutRCInPeriod: number;
  clientsWithoutRAEInPeriod: number;
  daysWithoutMeetingDistribution: {
    range_0_30: number;
    range_31_60: number;
    range_61_90: number;
    range_above_90: number;
  };
}

export interface CompanyJourneyResponseDto {
  summary: JourneySummary;
  priority: JourneyPriority;
  priorityRanking: JourneyClient[];
}

export interface ClientMeetingJourney {
  type: string;
  meetingType?: string;
  meta?: number;
  expected: number;
  completed: number;
  pending: number;
  compliance: number;
  status: string;
  riskLevel?: string;
  lastCompleted?: string | null;
  lastDue?: string | null;
  nextDue?: string | null;
  daysSinceLastMeeting?: number | null;
  daysOverdue?: number;
}

export interface ClientJourneyDetail {
  id_loja: number;
  cliente: string;
  cnpj?: string;
  consultor: string;
  journeyScore: number;
  data_inicio_monitoramento: string | null;
  riskLevel: string;
  lastMeetingType?: string | null;
  lastCompleted?: string | null;
  daysSinceLastMeeting?: number | null;
  meetings: ClientMeetingJourney[];
}

export function formatDateBR(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const cleanStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = cleanStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  } catch {
    return '—';
  }
}

export function translateRisk(risk: string | null | undefined): string {
  switch (risk) {
    case 'HEALTHY':
      return 'Saudável';
    case 'ATTENTION':
      return 'Atenção';
    case 'CRITICAL':
      return 'Crítico';
    case 'ABANDONED':
      return 'Abandonado';
    case 'NOT_STARTED':
    default:
      return 'Monitoramento não iniciado';
  }
}

export function getMeetingStatusLabelText(m: ClientMeetingJourney): string {
  if (m.expected === 0 && m.nextDue) {
    return 'Ainda não exigida';
  }
  switch (m.status) {
    case 'OK':
      return 'Em dia';
    case 'WARNING':
      return 'Parcialmente cumprida';
    case 'OVERDUE':
      return 'Atrasada';
    case 'NOT_APPLICABLE':
    default:
      return 'Sem obrigação no período';
  }
}

function getMeetingObservationsText(m: ClientMeetingJourney, dataInicio: string | null): string {
  const isRD = m.type === 'RD';
  const isRA = m.type === 'RA';
  const isRP = m.type === 'RP';
  const isFuture = m.expected === 0 && m.nextDue !== null;

  if (isRA) {
    if (m.lastCompleted) {
      return `Última: ${formatDateBR(m.lastCompleted)} ${m.daysSinceLastMeeting !== undefined && m.daysSinceLastMeeting !== null ? `(há ${m.daysSinceLastMeeting} dias)` : ''}`;
    }
    return 'Sem registros de RA para este cliente.';
  }

  if (isRP) {
    return 'Reunião oportunística sem registros rígidos.';
  }

  const parts: string[] = [];
  if (m.lastCompleted) {
    parts.push(`Realizada em: ${formatDateBR(m.lastCompleted)}`);
  }
  if (!isRD) {
    if (m.nextDue) {
      parts.push(`${isFuture ? 'Primeira obrigação prevista' : 'Próximo vencimento'}: ${formatDateBR(m.nextDue)}`);
    } else if (m.lastDue) {
      parts.push(`Último vencimento: ${formatDateBR(m.lastDue)}`);
    }
    if (m.daysOverdue && m.daysOverdue > 0) {
      parts.push(`Atraso: ${m.daysOverdue} dias`);
    }
  }

  return parts.join(' | ') || '—';
}

/**
 * Normaliza os dados detalhados do cliente para garantir todas as reuniões (RD, RE, RC, RI, RAE, RA, RP)
 */
function normalizeClientDetail(c: JourneyClient, detail: ClientJourneyDetail | null): ClientJourneyDetail {
  const id_loja = c.id_loja ?? c.id ?? 0;
  const cliente = c.razao_social ?? c.cliente ?? c.nome ?? '';
  const consultor = c.consultor ?? 'Não Informado';
  const riskLevel = detail?.riskLevel ?? c.riskLevel ?? 'NOT_STARTED';
  const journeyScore = detail?.journeyScore ?? c.complianceRate ?? c.journeyScore ?? 0;
  const data_inicio_monitoramento = detail?.data_inicio_monitoramento ?? null;
  const daysSinceLastMeeting = detail?.daysSinceLastMeeting ?? c.daysSinceLastMeeting ?? null;

  const meetingTypesOrder = ['RD', 'RE', 'RC', 'RI', 'RAE', 'RA', 'RP'];
  const existingMeetingsMap = new Map<string, ClientMeetingJourney>();
  if (detail?.meetings) {
    for (const m of detail.meetings) {
      existingMeetingsMap.set(m.type, m);
    }
  }

  const meetings: ClientMeetingJourney[] = meetingTypesOrder.map((type) => {
    if (existingMeetingsMap.has(type)) {
      return existingMeetingsMap.get(type)!;
    }
    return {
      type,
      meetingType: `Reunião ${type}`,
      expected: 0,
      completed: 0,
      pending: 0,
      compliance: 100,
      status: 'NOT_APPLICABLE',
    };
  });

  return {
    id_loja,
    cliente,
    consultor,
    journeyScore,
    data_inicio_monitoramento,
    riskLevel,
    daysSinceLastMeeting,
    meetings,
  };
}

/**
 * EXPORTAÇÃO EXCEL COM TABELA DETALHADA POR CLIENTE
 */
export async function exportJornadaToExcel(
  data: CompanyJourneyResponseDto,
  fetchClientDetail?: (id_loja: number) => Promise<ClientJourneyDetail | null>,
  fileName: string = 'relatorio_jornada_cliente'
) {
  if (!data) {
    alert('Não há dados para exportar.');
    return;
  }

  const workbook = XLSX.utils.book_new();

  // 1. Aba Resumo
  const summarySheet = [
    { metrica: 'Total de Clientes', valor: data.summary.totalClients },
    { metrica: 'Monitorados', valor: data.summary.monitorados },
    { metrica: 'Conformes (Saudáveis)', valor: data.summary.clientsHealthy },
    { metrica: 'Parcialmente Conformes (Atenção)', valor: data.summary.clientsAttention },
    { metrica: 'Críticos', valor: data.summary.clientsCritical },
    { metrica: 'Abandonados', valor: data.summary.clientsAbandoned },
    { metrica: 'Compliance Geral (Score Médio)', valor: `${data.summary.averageJourneyScore}%` },
    { metrica: 'Pendências Médias por Cliente', valor: data.summary.averagePendingPerClient },
    { metrica: 'Média Dias Sem Reunião', valor: data.summary.averageDaysWithoutMeeting },
    { metrica: 'Mediana Dias Sem Reunião', valor: data.summary.medianDaysWithoutMeeting },
    { metrica: 'Metas Previstas Total', valor: data.summary.totalMetasPrevistas },
    { metrica: 'Metas Cumpridas Total', valor: data.summary.totalMetasCumpridas },
    { metrica: 'Taxa de Compliance Geral (%)', valor: `${data.summary.meetingComplianceRate}%` },
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summarySheet);
  XLSX.utils.book_append_sheet(workbook, wsSummary, 'Resumo Geral');

  // Coletar todos os clientes
  const allClients: JourneyClient[] = [
    ...(data.priority.critical || []),
    ...(data.priority.warning || []),
    ...(data.priority.healthy || []),
    ...(data.priority.unmonitored || []),
  ];

  // Buscar detalhes individuais de cada cliente se a função de busca for fornecida
  const clientDetailsMap = new Map<number, ClientJourneyDetail>();
  if (fetchClientDetail && allClients.length > 0) {
    const ids = Array.from(new Set(allClients.map((c) => c.id_loja ?? c.id ?? 0).filter(Boolean)));
    const details = await Promise.all(ids.map((id) => fetchClientDetail(id).catch(() => null)));
    details.forEach((det, index) => {
      if (det) clientDetailsMap.set(ids[index], det);
    });
  }

  // 2. Aba Detalhamento por Cliente
  const detailRows: any[] = [];

  for (const c of allClients) {
    const id = c.id_loja ?? c.id ?? 0;
    const detail = normalizeClientDetail(c, clientDetailsMap.get(id) || null);

    // Linha do Cliente (Header)
    detailRows.push({
      'Cliente / Reunião': `CLIENTE: ${detail.id_loja} - ${detail.cliente}`,
      'Consultor / Status': `Consultor: ${detail.consultor}`,
      'Início Monitoramento': `Início: ${formatDateBR(detail.data_inicio_monitoramento)}`,
      'Nível Risco': `Risco: ${translateRisk(detail.riskLevel)}`,
      'Esperadas': '',
      'Realizadas': '',
      'Pendentes': '',
      'Cumprimento (%)': `Cumprimento Meta: ${detail.journeyScore}%`,
      'Observações / Datas': detail.daysSinceLastMeeting !== null ? `Inatividade: ${detail.daysSinceLastMeeting} dias` : 'Inatividade: —',
    });

    // Sub-cabeçalho de reuniões
    detailRows.push({
      'Cliente / Reunião': '  Tipo de Reunião',
      'Consultor / Status': 'Status',
      'Início Monitoramento': '',
      'Nível Risco': '',
      'Esperadas': 'Esperadas',
      'Realizadas': 'Realizadas',
      'Pendentes': 'Pendentes',
      'Cumprimento (%)': 'Cumprimento (%)',
      'Observações / Datas': 'Observações / Datas',
    });

    // Linhas de reuniões do cliente (RD, RE, RC, RI, RAE, RA, RP)
    for (const m of detail.meetings) {
      const isRA = m.type === 'RA';
      const isRP = m.type === 'RP';

      detailRows.push({
        'Cliente / Reunião': `    Reunião ${m.type}`,
        'Consultor / Status': getMeetingStatusLabelText(m),
        'Início Monitoramento': '',
        'Nível Risco': '',
        'Esperadas': isRA || isRP ? '—' : m.expected,
        'Realizadas': m.completed,
        'Pendentes': isRA || isRP ? '—' : m.pending,
        'Cumprimento (%)': isRA || isRP ? '—' : `${m.compliance}%`,
        'Observações / Datas': getMeetingObservationsText(m, detail.data_inicio_monitoramento),
      });
    }

    // Linha em branco separadora
    detailRows.push({});
  }

  const wsDetail = XLSX.utils.json_to_sheet(detailRows);
  XLSX.utils.book_append_sheet(workbook, wsDetail, 'Detalhamento por Cliente');

  // 3. Aba Lista Geral de Clientes
  const flatClientsList = allClients.map((c) => ({
    ID: c.id_loja ?? c.id ?? '',
    'Razão Social': c.razao_social ?? c.cliente ?? c.nome ?? '',
    Consultor: c.consultor ?? '',
    'Nível Risco': translateRisk(c.riskLevel),
    'Compliance (%)': `${c.complianceRate ?? c.journeyScore ?? 0}%`,
    'Pendências Totais': c.totalPending ?? 0,
    'Score Prioridade': c.priorityScore ?? 0,
    'Dias Sem Reunião': c.daysSinceLastMeeting ?? 'N/A',
  }));
  const wsFlat = XLSX.utils.json_to_sheet(flatClientsList);
  XLSX.utils.book_append_sheet(workbook, wsFlat, 'Lista de Clientes');

  // Gravar arquivo
  XLSX.writeFile(workbook, `${fileName.replace(/\s+/g, '_').toLowerCase()}.xlsx`);
}

/**
 * EXPORTAÇÃO PDF COM TABELA DETALHADA POR CLIENTE
 */
export async function exportJornadaToPDF(
  data: CompanyJourneyResponseDto,
  fetchClientDetail?: (id_loja: number) => Promise<ClientJourneyDetail | null>,
  fileName: string = 'relatorio_jornada_cliente'
) {
  if (!data) {
    alert('Não há dados para exportar.');
    return;
  }

  const doc = new jsPDF('portrait');
  let page = 1;

  const addHeader = (title: string) => {
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(title, 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 21);
    doc.text(`Página ${page}`, 196, 15, { align: 'right' });
    doc.setDrawColor(226, 232, 240);
    doc.line(14, 24, 196, 24);
  };

  // --- Página 1: Resumo Executivo ---
  addHeader('Relatório da Jornada do Cliente - Resumo Executivo');

  const summaryData = [
    ['Total de Clientes', String(data.summary.totalClients)],
    ['Clientes Monitorados', String(data.summary.monitorados)],
    ['Saudáveis (Conformes)', String(data.summary.clientsHealthy)],
    ['Atenção (Parcialmente Conformes)', String(data.summary.clientsAttention)],
    ['Críticos', String(data.summary.clientsCritical)],
    ['Abandonados', String(data.summary.clientsAbandoned)],
    ['Compliance Geral (Score Médio)', `${data.summary.averageJourneyScore}%`],
    ['Taxa de Cumprimento de Reuniões (%)', `${data.summary.meetingComplianceRate}%`],
    ['Metas Previstas / Cumpridas', `${data.summary.totalMetasPrevistas} / ${data.summary.totalMetasCumpridas}`],
    ['Pendências Médias por Cliente', String(data.summary.averagePendingPerClient)],
    ['Média de Dias sem Reunião', String(data.summary.averageDaysWithoutMeeting)],
  ];

  autoTable(doc, {
    head: [['Métrica', 'Valor']],
    body: summaryData,
    startY: 28,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 14, right: 14 },
  });

  // Coletar todos os clientes
  const allClients: JourneyClient[] = [
    ...(data.priority.critical || []),
    ...(data.priority.warning || []),
    ...(data.priority.healthy || []),
    ...(data.priority.unmonitored || []),
  ];

  // Buscar detalhes de cada cliente se a função de busca for fornecida
  const clientDetailsMap = new Map<number, ClientJourneyDetail>();
  if (fetchClientDetail && allClients.length > 0) {
    const ids = Array.from(new Set(allClients.map((c) => c.id_loja ?? c.id ?? 0).filter(Boolean)));
    const details = await Promise.all(ids.map((id) => fetchClientDetail(id).catch(() => null)));
    details.forEach((det, index) => {
      if (det) clientDetailsMap.set(ids[index], det);
    });
  }

  // --- Páginas de Detalhamento por Cliente ---
  doc.addPage('portrait');
  page++;
  addHeader('Detalhamento da Jornada por Cliente');

  let currentY = 28;

  for (const c of allClients) {
    const id = c.id_loja ?? c.id ?? 0;
    const detail = normalizeClientDetail(c, clientDetailsMap.get(id) || null);

    // Se o espaço restante na página for menor que 85 unidades, adicione uma nova página
    if (currentY > 230) {
      doc.addPage('portrait');
      page++;
      addHeader('Detalhamento da Jornada por Cliente');
      currentY = 28;
    }

    // Banner do Cliente
    doc.setFillColor(30, 41, 59);
    doc.rect(14, currentY, 182, 16, 'F');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`${detail.id_loja} ${detail.cliente}`, 18, currentY + 7);
    doc.setFontSize(8.5);
    doc.text(`Consultor: ${detail.consultor}`, 140, currentY + 7);

    // Sub-banner de status
    doc.setFillColor(241, 245, 249);
    doc.rect(14, currentY + 16, 182, 10, 'F');
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(8);
    const monitorDate = formatDateBR(detail.data_inicio_monitoramento);
    const riskText = translateRisk(detail.riskLevel);
    const inactivityText = detail.daysSinceLastMeeting !== null ? `${detail.daysSinceLastMeeting} dias` : '—';
    doc.text(
      `Início Monitoramento: ${monitorDate}  |  Risco: ${riskText}  |  Meta Jornada: ${detail.journeyScore}%  |  Inatividade: ${inactivityText}`,
      18,
      currentY + 22.5
    );

    // Tabela de Reuniões do Cliente
    const tableBody = detail.meetings.map((m) => {
      const isRA = m.type === 'RA';
      const isRP = m.type === 'RP';

      return [
        `Reunião ${m.type}`,
        getMeetingStatusLabelText(m),
        isRA || isRP ? '—' : String(m.expected),
        String(m.completed),
        isRA || isRP ? '—' : String(m.pending),
        isRA || isRP ? '—' : `${m.compliance}%`,
        getMeetingObservationsText(m, detail.data_inicio_monitoramento),
      ];
    });

    autoTable(doc, {
      head: [['Reunião', 'Status', 'Esperadas', 'Realizadas', 'Pendentes', 'Cumprimento', 'Observações / Datas']],
      body: tableBody,
      startY: currentY + 26,
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 2.5 },
      headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 26, fontStyle: 'bold' },
        1: { cellWidth: 32 },
        2: { cellWidth: 16, halign: 'center' },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 16, halign: 'center' },
        5: { cellWidth: 20, halign: 'center' },
        6: { cellWidth: 'auto' },
      },
      margin: { left: 14, right: 14 },
    });

    // Atualiza a posição Y para o próximo cliente
    currentY = (doc as any).lastAutoTable.finalY + 12;
  }

  doc.save(`${fileName.replace(/\s+/g, '_').toLowerCase()}.pdf`);
}
